import React, { useMemo, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Alert, Button, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import EnhancedTable from "../../../components/common/EnhancedTable";
import { useFeeReceipts } from "../../../hooks/useFeeReceipt";
import { useError } from "../../../contexts/ErrorContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FeeReceiptTemplate from "../../../components/templates/FeeReceiptTemplate";
import { FeeReceipt } from "../../../services/paymentService";
import CommonModal from "../../../components/common/CommonModal";

const FeeReceiptsManagement: React.FC = () => {
    const { selectedSession, selectedClass } = useSessionClass();
    const { t } = useTranslation();
    const { showError } = useError();

    const [selectedReceipt, setSelectedReceipt] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const { data, isLoading, error } = useFeeReceipts(
        selectedClass?.id,
        selectedSession?.id
    );

    const getTimeValue = (date: any) => {
        if (!date) return 0;
        if (Array.isArray(date)) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = date;
            return new Date(year, month - 1, day, hour, minute, second).getTime();
        }
        return new Date(date).getTime();
    };

    const handleView = (receipt: any) => {
        // Map new API response DTO -> FeeReceiptTemplate expected shape
        const feeTx = receipt.feeTransactionDto || {};
        const acct = feeTx.studentAccountsResponseDto;
        const feeTransactionMapped = {
            ...feeTx,
            // FeeReceiptTemplate expects `feeTransaction.studentAccount.student...`
            studentAccount: acct
                ? {
                    id: acct.id,
                    studentAccountNumber: acct.studentAccountNumber,
                    dueAmount: acct.dueAmount,
                    student: acct.student,
                }
                : feeTx.studentAccount,
        };
        const mapped: FeeReceipt = {
            id: receipt.id,
            localDateTime: receipt.localDateTime,
            feeTransaction: feeTransactionMapped,
            particulars: receipt.feeParticulars,
        } as any;
        setSelectedReceipt(mapped);
    };

    const tableData = useMemo(() => {
        if (!data) return [];

        // Sort data by recent date first
        const sortedData = [...data].sort((a, b) => {
            const dateA = getTimeValue(a.localDateTime || a.feeTransactionDto?.createdAt);
            const dateB = getTimeValue(b.localDateTime || b.feeTransactionDto?.createdAt);
            return dateB - dateA;
        });

        return sortedData.map((receipt) => ({
            ...receipt,
            studentName:
                receipt.feeTransactionDto?.studentAccountsResponseDto?.student?.studentName ||
                receipt.feeTransactionDto?.studentAccount?.student?.studentName ||
                "N/A",
            paymentType: receipt.feeTransactionDto?.paymentType || "N/A",
            amount: receipt.feeTransactionDto?.amount || 0,
            paymentId: receipt.feeTransactionDto?.transactionId || "N/A",
            status: receipt.feeTransactionDto?.status || "SUCCESS",
        }));
    }, [data]);

    const columns = [
        {
            id: "studentName",
            label: t("student_name"),
            minWidth: 150
        },
        {
            id: "paymentType",
            label: t("payment_type"),
            minWidth: 120
        },
        {
            id: "amount",
            label: t("amount"),
            minWidth: 100,
            align: "right" as const,
            format: (value: number) => `₹${value?.toLocaleString() || "0"}`
        },
        {
            id: "paymentId",
            label: t("payment_id"),
            minWidth: 150
        },
        {
            id: "status",
            label: t("status"),
            minWidth: 100,
            format: (value: string) => (
                <Typography
                    variant="body2"
                    sx={{
                        color: value === "SUCCESS" ? "success.main" : "error.main",
                        fontWeight: "bold"
                    }}
                >
                    {value}
                </Typography>
            )
        },
        {
            id: "view",
            label: t("view"),
            minWidth: 100,
            align: "center" as const,
            format: (_: any, row: any) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleView(row);
                    }}
                    sx={{
                        textTransform: "none",
                        borderRadius: 10,
                        boxShadow: "none",
                        bgcolor:"#3092e3",
                        px: 2,
                        "&:hover": {
                            boxShadow: "none",
                            bgcolor: "primary.main",
                        }
                    }}
                >
                    {t("view")}
                </Button>
            )
        },
    ];

    const isActuallyLoading = isLoading && !!selectedClass?.id && !!selectedSession?.id;

    if (isActuallyLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{t("error_loading_receipts")}</Alert>
            </Box>
        );
    }

    return (
        <Paper sx={{ p: 1.5, maxWidth: 1500, mx: 'auto', mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                        fontSize: "1.25rem",
                        whiteSpace: "nowrap",
                    }}
                >
                    {t("fee_receipts")}
                </Typography>
                <TextField
                    size="small"
                    placeholder={t("search")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
                />
            </Box>

            {(!selectedSession || !selectedClass) && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    {t("please_select_session_and_class_for_receipts") || "Please select a session and class"}
                </Alert>
            )}
            <EnhancedTable
                columns={columns}
                data={tableData}
                searchFields={["studentName", "paymentType", "paymentId"]}
                hideSearch
                externalSearchTerm={searchTerm}
                cardProps={{
                    elevation: 0,
                    sx: {
                        border: 'none',
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        mb: 0,
                        '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
                    },
                }}
            />

            <CommonModal
                open={!!selectedReceipt}
                onClose={() => setSelectedReceipt(null)}
                title={t('fee_receipt') || 'Fee Receipt'}
                maxWidth="lg"
                fullWidth
                hideActions
                PaperProps={{ sx: { maxHeight: "90vh" } }}
                contentSx={{ p: 2, overflow: "auto" }}
            >
                {selectedReceipt && (
                    <FeeReceiptTemplate
                        receipt={selectedReceipt}
                    />
                )}
            </CommonModal>
        </Paper>
    );
};

export default FeeReceiptsManagement;
